import { IconLayer } from '@deck.gl/layers';
import type { LayerState, LayerFeature, HarborStationProps } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { buoyGlyph, type IconDef } from './markerGlyphs.js';

/** Cyan-marine fill for the buoy float. */
const BUOY_COLOR: [number, number, number] = [0, 204, 200];

/** Glyph render size in pixels. */
const BUOY_SIZE_PX = 28;

interface BuoyDatum {
  position: [number, number];
  icon: IconDef;
  /** Carry the full props so the tooltip can read them. */
  props: HarborStationProps;
}

/**
 * Renders harbor buoy stations as pickable navigation-buoy IconLayer markers.
 * Returns null when the feed is down or has no features.
 */
export function buildHarborLayer(
  state: LayerState,
  _nowSec: number,
): IconLayer<BuoyDatum> | null {
  if (!state || state.status === 'down' || state.features.length === 0) {
    return null;
  }

  const icon = buoyGlyph(BUOY_COLOR, BUOY_SIZE_PX);
  const data: BuoyDatum[] = state.features.map((f: LayerFeature) => ({
    position: (f.geometry as { coordinates: [number, number] }).coordinates,
    icon,
    props: f.props as unknown as HarborStationProps,
  }));

  return new IconLayer<BuoyDatum>({
    id: 'harbor',
    data,
    getIcon: (d) => d.icon,
    getPosition: (d) => d.position,
    getSize: BUOY_SIZE_PX,
    sizeUnits: 'pixels',
    billboard: true,
    pickable: true,
  });
}

export const harborRenderer: RendererDef = {
  layerId: 'harbor',
  build: (state, nowSec) => buildHarborLayer(state, nowSec),
};
